/**
 * pages/Goals.tsx — the objectives this business unit has registered, and the box that adopts one.
 *
 * The screen answers three questions: which objectives are being tracked here, how stale is what we
 * know about them, and where does a check-in start. Everything about a single objective — its key
 * results, its history — is the record page behind it.
 *
 * **Every row on this table is a SNAPSHOT, and the table says so.** `06-modules.md` §5.3 R1: a record
 * synchronised from another system states its source and its last sync, and must not present a
 * synchronised figure with the confidence of a live one. So the connection's site is named above the
 * table and `syncedAt` is a column rather than a detail on the record page — a reader comparing five
 * objectives needs to know which of them was last read a fortnight ago.
 *
 * **A missing connection is not an empty list.** `GET /api/goals` answers `connection: null` rather than
 * omitting the field for exactly this reason: with no connector there is nothing to add a goal to, and
 * an empty table under an enabled "Add a goal" button would offer a control whose only possible outcome
 * is a refusal. The banner replaces the button rather than accompanying it.
 */
import { useCallback, useState } from "react";
import {
  ApiError,
  fetchGoals,
  goalErrorMessage,
  registerGoal,
  removeGoal,
  type GoalListResponse,
  type GoalView,
} from "../lib/api";
import { formatDateTime, formatRelative } from "../lib/format";
import { orNotRead, useAsync } from "./goalsShared";
import {
  Banner,
  Button,
  DeniedState,
  ErrorState,
  LoadingState,
  Modal,
  Page,
  Table,
  TextField,
  useToast,
  type Column,
} from "../components/ui";

export interface GoalsPorts {
  readonly load: (signal: AbortSignal) => Promise<GoalListResponse>;
  readonly add: (reference: string) => Promise<GoalView>;
  readonly remove: (goalId: string) => Promise<void>;
}

const LIVE_PORTS: GoalsPorts = {
  load: (signal) => fetchGoals(signal),
  add: registerGoal,
  remove: removeGoal,
};

export interface GoalsProps {
  readonly buId: string;
  /** From `/api/me`'s per-scope roles. Hiding is UX; the server re-checks `job_author` on every write. */
  readonly canAuthor?: boolean;
  readonly ports?: GoalsPorts;
}

export function Goals({ buId, canAuthor = false, ports = LIVE_PORTS }: GoalsProps): JSX.Element {
  const toast = useToast();
  const { load, add, remove } = ports;
  const goals = useAsync((signal) => load(signal), `goals:${buId}`);

  const [adding, setAdding] = useState(false);
  const [reference, setReference] = useState("");
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState<ApiError | null>(null);

  const [removing, setRemoving] = useState<GoalView | null>(null);
  const [removeBusy, setRemoveBusy] = useState(false);

  const connection = goals.data?.connection ?? null;
  const rows = goals.data?.goals ?? null;

  const closeAdd = useCallback((): void => {
    setAdding(false);
    setReference("");
    setAddError(null);
  }, []);

  const submitAdd = (): void => {
    const trimmed = reference.trim();
    if (trimmed === "" || addBusy) return;
    setAddBusy(true);
    setAddError(null);
    void add(trimmed)
      .then((goal) => {
        closeAdd();
        toast.show(`“${goal.name}” added`, { tone: "success", detail: `${goal.key} · check in on it from its record.` });
        goals.reload();
      })
      .catch((cause: unknown) => {
        setAddError(cause instanceof ApiError ? cause : new ApiError(0, "internal_error", String(cause), null));
      })
      .finally(() => setAddBusy(false));
  };

  const confirmRemove = (): void => {
    const target = removing;
    if (target === null || removeBusy) return;
    setRemoveBusy(true);
    void remove(target.id)
      .then(() => {
        setRemoving(null);
        toast.show(`“${target.name}” removed`, {
          tone: "info",
          detail: "Nothing in Atlassian changed — only this list.",
        });
        goals.reload();
      })
      .catch((cause: unknown) => {
        const failure = cause instanceof ApiError ? cause : new ApiError(0, "internal_error", String(cause), null);
        toast.show("That objective could not be removed", { tone: "danger", detail: goalErrorMessage(failure) });
      })
      .finally(() => setRemoveBusy(false));
  };

  /** Drawn only where it could actually succeed: authoring role, AND a connector to add the goal to. */
  const addButton =
    canAuthor && connection !== null ? (
      <Button variant="primary" onClick={() => setAdding(true)}>
        Add a goal
      </Button>
    ) : undefined;

  const columns: readonly Column<GoalView>[] = [
    {
      key: "name",
      header: "Objective",
      type: "text",
      value: (row) => row.name,
      render: (row) => <a href={`#/goals/${encodeURIComponent(row.id)}`}>{row.name}</a>,
    },
    {
      key: "key",
      header: "Key",
      type: "text",
      value: (row) => row.key,
      render: (row) => (
        // Out to Atlassian rather than to the record page: the key IS the Atlassian address, and a reader
        // who clicks it wants the thing itself. The objective's own name, beside it, is the way in here.
        <a href={row.url} target="_blank" rel="noopener noreferrer">
          {row.key}
        </a>
      ),
    },
    {
      key: "owner",
      header: "Owner",
      type: "text",
      value: (row) => row.ownerName,
      // Not the table's default em dash: an owner Atlassian did not return is a field we could not read,
      // which is a different fact from an objective that has none. §5.3 R2.
      render: (row) => orNotRead(row.ownerName),
    },
    {
      key: "syncedAt",
      header: "Last synced",
      type: "date",
      value: (row) => row.syncedAt,
      // §5.3 R1. The relative form is what makes staleness legible at a glance; the absolute one is in
      // the title, because "2 days ago" cannot be matched against anything.
      render: (row) => <time title={formatDateTime(row.syncedAt)}>{formatRelative(row.syncedAt)}</time>,
    },
    {
      key: "addedAt",
      header: "Added",
      type: "date",
      value: (row) => row.addedAt,
      render: (row) => <time title={formatDateTime(row.addedAt)}>{formatRelative(row.addedAt)}</time>,
    },
    {
      key: "actions",
      header: "Actions",
      actions: true,
      // One of the four permitted unsortable exemptions: an action column has no value to order by.
      sortable: false,
      render: (row) => (
        <span className="row">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => (window.location.hash = `#/goals/${encodeURIComponent(row.id)}`)}
          >
            Open
          </Button>
          {canAuthor ? (
            <Button
              variant="link"
              size="sm"
              onClick={() => setRemoving(row)}
              title="Stop tracking this objective here. Nothing in Atlassian is changed."
            >
              Remove
            </Button>
          ) : null}
        </span>
      ),
    },
  ];

  // ⚠️ The site is named in the page's LEDE, and the placement is the clause's rather than a preference.
  // `06-modules.md` §422: where a record is synchronised from another system, "the header MUST state the
  // source and when it was last synchronised". The `Last synced` column is the *when*; this is the
  // *where*. Both used to sit in a standing banner above the table, removed on 7 August 2026 as noise —
  // but only the posting-account sentence was noise; the source obligation survived it and had to move
  // rather than go, or the screen becomes a set of figures from nowhere.
  //
  // NOT the table's caption, which is where this went first. `Table` builds the mobile sort control's
  // accessible name from the caption ("Sort Objectives by"), so a caption carrying provenance produces
  // "Sort Objectives, read from tropicalsun.atlassian.net by" — a caption is a name, and a name is not a
  // place to put a second fact.
  //
  // ⚠️ The lede is now the CLAUSE AND NOTHING ELSE, on the owner's instruction of 10 August 2026. It also
  // carried two sentences explaining what a check-in is and what it does not post — read once, in the way
  // of the screen thereafter, and on a phone occupying a fifth of the first view above the objectives it
  // was introducing. The obligation is not what was removed: an absent connection renders no lede at all,
  // because there is no source to name, and the warning banner below states the reason in its place.
  const lede = connection === null ? undefined : `Read from ${connection.site}.`;

  return (
    <Page
      title="Goals"
      {...(lede === undefined ? {} : { lede })}
      {...(addButton === undefined ? {} : { actions: addButton })}
    >
      {connection === null && !goals.loading && goals.error === null ? (
        <Banner tone="warning">
          This business unit is not connected to Atlassian Goals, so nothing can be read or posted. An
          administrator sets the connection up in the deployment&rsquo;s configuration.
        </Banner>
      ) : null}

      {goals.loading ? <LoadingState label="Loading goals" /> : null}

      {goals.error !== null ? (
        goals.error.isPermissionDenied ? (
          <DeniedState body={goalErrorMessage(goals.error)} />
        ) : (
          <ErrorState
            message={goalErrorMessage(goals.error)}
            traceId={goals.error.traceId}
            onRetry={goals.reload}
          />
        )
      ) : null}

      {rows !== null ? (
        <Table
          caption="Objectives"
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          defaultSort={{ key: "name", direction: "ascending" }}
          titleKey="name"
          mobile="cards"
          empty={{
            title: "No objectives are registered in this business unit yet",
            body:
              connection === null
                ? "Once an administrator connects this business unit to Atlassian Goals, paste an objective's address here and its key results become checkable."
                : canAuthor
                  ? "Add one by pasting its address from Atlassian — or its key, such as TROPI8-667. Its key results are read when a check-in starts, not before."
                  : "Adding an objective needs the goal-author role in this business unit; an administrator grants it through a group.",
            ...(addButton === undefined ? {} : { action: addButton }),
          }}
        />
      ) : null}

      <Modal
        open={adding}
        title="Add a goal"
        description="Paste the address of the objective from your browser, or type its key. Its key results are its child goals in Atlassian, and they are read when you start a check-in."
        onClose={closeAdd}
        actions={
          <>
            <Button variant="ghost" onClick={closeAdd}>
              Cancel
            </Button>
            <Button variant="primary" onClick={submitAdd} loading={addBusy} disabled={reference.trim() === ""}>
              Add goal
            </Button>
          </>
        }
      >
        <div className="stack">
          {addError !== null ? (
            <>
              {/* The server's own sentence, not ours — see `goalErrorMessage`. Every refusal this box can
                  produce names what to do about it, and a mapped code could not. */}
              <Banner tone="danger">{goalErrorMessage(addError)}</Banner>
              {addError.traceId === null || addError.traceId === "" ? null : (
                <p className="field__hint mono">Trace {addError.traceId}</p>
              )}
            </>
          ) : null}
          <TextField
            label="Objective address or key"
            value={reference}
            onChange={setReference}
            placeholder="https://home.atlassian.net/o/…/goal/TROPI8-667/about"
            hint="A pasted URL, a key like TROPI8-667, or an ARI."
            required
          />
        </div>
      </Modal>

      <Modal
        open={removing !== null}
        role="alertdialog"
        title="Remove this objective?"
        onClose={() => setRemoving(null)}
        actions={
          <>
            <Button variant="ghost" onClick={() => setRemoving(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmRemove} loading={removeBusy}>
              Remove
            </Button>
          </>
        }
      >
        <p>
          “{removing?.name}” ({removing?.key}) stops being tracked here, along with its check-in history
          on this screen. <strong>Nothing in Atlassian is changed</strong> — check-ins already posted stay
          exactly where they are, because a goal update cannot be withdrawn.
        </p>
      </Modal>
    </Page>
  );
}
